// LoginComponent.jsx — split-panel auth layout matching the site's editorial theme
import React, { useEffect, useRef, useState } from "react";
import { withRouter, Link } from "react-router-dom";
import { FaEnvelope, FaLock, FaShieldAlt, FaCheckCircle } from "react-icons/fa";
import AuthenticationService from "./AuthenticationService.js";

const TRUST_POINTS = [
  "Save progress on quizzes and proficiency tests",
  "Earn and manage shareable certificates",
  "Contribute questions to the community bank",
];

function LoginComponent({ history }) {
  const [form, setForm] = useState({ username: "", password: "" });
  const [touched, setTouched] = useState({ username: false, password: false });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const userRef = useRef(null);

  useEffect(() => {
    if (userRef.current) userRef.current.focus();
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const validate = () => {
    const errs = {
      username: !form.username.trim(),
      password: !form.password.trim(),
    };
    return { errs, ok: !errs.username && !errs.password };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({ username: true, password: true });
    setErrorMsg("");

    const { ok } = validate();
    if (!ok) return;

    try {
      setLoading(true);
      const resp = await AuthenticationService.executeJwtAuthenticationService(
        form.username,
        form.password
      );
      AuthenticationService.registerSuccessfulLoginForJwt(
        form.username,
        resp.data.token
      );
      history.push(`/welcome/${form.username}`);
    } catch (err) {
      setErrorMsg("Invalid username or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const { errs } = validate();

  return (
    <div className="tv-auth-shell">
      {/* Branding panel — hidden on small screens, see .tv-auth-brand CSS */}
      <div className="tv-auth-brand">
        <div className="tv-auth-brand-inner">
          <span className="tv-eyebrow">Tech Venture</span>
          <h1>Pick up right where you left off</h1>
          <p>
            Sign in to track your progress across quizzes, proficiency tests,
            and certificates.
          </p>
          <ul className="tv-auth-trust-list">
            {TRUST_POINTS.map((point) => (
              <li key={point}>
                <FaCheckCircle aria-hidden="true" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Form panel */}
      <div className="tv-auth-form-side">
        <div className="tv-auth-form-card">
          <div className="text-center mb-4 d-lg-none">
            <span className="badge bg-primary-subtle text-primary mb-2">
              Tech Venture
            </span>
          </div>
          <h2 className="h4 mb-1">Welcome back</h2>
          <p className="text-muted mb-4">Sign in to continue</p>

          {errorMsg && (
            <div
              className="alert alert-danger py-2 small"
              role="alert"
              aria-live="assertive"
            >
              {errorMsg}
            </div>
          )}

          <form onSubmit={onSubmit} noValidate>
            <div className="mb-3">
              <label htmlFor="username" className="form-label">
                Username
              </label>
              <div className="input-group">
                <span className="input-group-text" id="user-addon">
                  <FaEnvelope aria-hidden="true" />
                </span>
                <input
                  ref={userRef}
                  id="username"
                  name="username"
                  type="text"
                  className={`form-control ${
                    touched.username && errs.username ? "is-invalid" : ""
                  }`}
                  placeholder="you@example.com"
                  value={form.username}
                  onChange={onChange}
                  onBlur={() => setTouched((t) => ({ ...t, username: true }))}
                  aria-describedby="user-addon"
                  aria-invalid={touched.username && errs.username}
                  autoComplete="username"
                />
                <div className="invalid-feedback">
                  Please enter your username or email.
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <FaLock aria-hidden="true" />
                </span>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className={`form-control ${
                    touched.password && errs.password ? "is-invalid" : ""
                  }`}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={onChange}
                  onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                  aria-invalid={touched.password && errs.password}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={0}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
                <div className="invalid-feedback">
                  Please enter your password.
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="remember"
                />
                <label className="form-check-label" htmlFor="remember">
                  Remember me
                </label>
              </div>
              <a
                href="mailto:info@techventurellc.com?subject=Password%20help"
                className="small text-decoration-none"
              >
                Forgot password?
              </a>
            </div>

            <div className="d-grid">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                aria-busy={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </div>
          </form>

          <p className="tv-auth-fineprint">
            <FaShieldAlt aria-hidden="true" /> By continuing, you agree to our{" "}
            <Link to="/privacyPolicyMain">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default withRouter(LoginComponent);
